import VisuallyHidden from '@reach/visually-hidden';
import Image from 'next/image';
import { RichText } from 'prismic-reactjs';
import { useEffect, useState } from 'react';
import ContentContainer from 'remote-com-shared/components/ContentContainer';
import { fluidProp } from 'remote-com-shared/style/helpers';
import styled, { css } from 'styled-components';

import BoardIcon from './assets/board.svg';
import LinkedinIcon from './assets/linkedin.svg';
import BgIllustration from './assets/speakers-image-bg.svg';
import TwitterIcon from './assets/twitter.svg';

import { transformProperties } from '@/src/domains/prismic/helpers';
import { tabletBreakpoint, fromDesktopBreakpoint } from '@/src/domains/shared/breakpoints';

export const SpeakersSection = styled.section`
  color: var(--colors-blank);
  background-color: #5700ff;
  display: flex;
  ${fluidProp('padding-top', '80px', '114px')}
  position: relative;
`;

export const SectionHeader = styled.div`
  text-align: center;
`;

export const SectionHeaderTitle = styled.div`
  font-family: var(--typography-fontBrand);
  color: var(--colors-blank);
  ${({ theme }) => theme.typography.scale3}
  ${fluidProp(['font-size'], '32px', '40px')}

    > * {
    font-size: inherit;
    font-weight: inherit;
    margin: inherit;
  }
`;

export const SectionHeaderDescription = styled.div`
  font-family: var(--typography-fontBrand);
  color: var(--colors-moonRaker);
  ${({ theme }) => theme.typography.bodyLead}
  margin-top: 10px;

  > * {
    font-size: inherit;
    font-weight: inherit;
    margin: inherit;
  }
`;

export const SectionGrid = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 47px;
  margin-top: 182px;
`;

export const SpeakerContainer = styled.div`
  background: var(--colors-blank);
  box-shadow: 0px 12px 48px rgba(96, 101, 123, 0.24);
  border-radius: 10px;
  padding: 116px 33px 45px 28px;
  max-width: 284px;
  text-align: center;
  box-sizing: border-box;
  position: relative;
  margin-bottom: 130px;

  min-height: ${({ minCardHeight }) => minCardHeight}px;
`;

export const SpeakerImage = styled.div`
  position: absolute;
  left: 0;
  right: 0;

  ${({ $imageBottom }) => {
    switch ($imageBottom) {
      case true:
        return css`
          top: -12%;
        `;
      case false:
      default:
        return css`
          top: -19%;
        `;
    }
  }}
`;

export const SpeakerName = styled.div`
  font-family: var(--typography-fontBrand);
  color: var(--colors-irisBlue);
  ${({ theme }) => theme.typography.titleLG}
  font-weight: 600;

  > * {
    font-size: inherit;
    font-weight: inherit;
    margin: inherit;
  }
`;

export const SpeakerTitle = styled.div`
  ${({ theme }) => theme.typography.bodyXS}
  color: var(--colors-darkBlue);
  text-transform: uppercase;
  font-weight: 500;
  margin-top: 6px;

  > * {
    font-size: inherit;
    font-weight: inherit;
    margin: inherit;
  }
`;

export const SpeakerDescription = styled.div`
  font-family: var(--typography-fontBrand);
  ${({ theme }) => theme.typography.bodyLead}
  color: var(--colors-darkBlue);
  margin-top: 21px;
  margin-bottom: 45px;

  > * {
    font-size: inherit;
    font-weight: inherit;
    margin: inherit;
  }
`;

export const SocialLinks = styled.div`
  display: flex;
  justify-content: center;
  gap: 21px;
  width: 60px;
`;

export const SocialLinksWrapper = styled.div`
  position: absolute;
  bottom: 44px;
  left: 111px;
`;

export const IllustrationWrapper = styled.div`
  position: absolute;
  right: 0;
  left: 0;
  bottom: 0;
  z-index: 0;

  ${tabletBreakpoint(css`
    display: none;
  `)};
`;

export const Container = styled(ContentContainer)`
  z-index: 1;

  ${fromDesktopBreakpoint(css`
    max-width: unset;
  `)};
`;

export const ImageWrapper = styled.div`
  border: 10px var(--colors-blank) solid;
  border-radius: 50%;
  background-color: var(--colors-blank);
  overflow: hidden;
`;

export const ImageContainer = styled.div`
  display: flex;
  justify-content: center;
`;

export function SpeakersSlice({ slice }) {
  const { primary, fields: speakers } = transformProperties(slice);
  const { speakersTitle, speakersDescription } = primary;
  const [minCardHeight, setMinCardHeight] = useState();

  useEffect(() => {
    const cards = Array.from(document.getElementById('speakers-grid').children);
    const arrayCardsHeight = cards.map((card) => card?.clientHeight);
    setMinCardHeight(Math.max(...arrayCardsHeight));
  }, []);

  return (
    <SpeakersSection>
      <Container>
        <SectionHeader>
          <BoardIcon />
          {speakersTitle && <RichText render={speakersTitle} Component={SectionHeaderTitle} />}
          {speakersDescription && (
            <RichText render={speakersDescription} Component={SectionHeaderDescription} />
          )}
        </SectionHeader>
        <SectionGrid id="speakers-grid">
          {speakers.map((speaker, index) => {
            const {
              speakerName,
              speakerDescription,
              speakerTitle,
              illustration,
              linkedinLink,
              twitterLink,
              imageBottom,
            } = speaker;
            return (
              <SpeakerContainer key={index} minCardHeight={minCardHeight}>
                <SpeakerImage $imageBottom={imageBottom}>
                  <ImageContainer>
                    <ImageWrapper>
                      <Image
                        src={illustration.url}
                        alt={illustration.alt || ''}
                        width={illustration.dimensions.width}
                        height={illustration.dimensions.height}
                        objectFit="cover"
                        objectPosition="center top"
                        quality="100"
                      />
                    </ImageWrapper>
                  </ImageContainer>
                </SpeakerImage>
                {speakerName && <RichText render={speakerName} Component={SpeakerName} />}
                {speakerTitle && <RichText render={speakerTitle} Component={SpeakerTitle} />}
                {speakerDescription && (
                  <RichText render={speakerDescription} Component={SpeakerDescription} />
                )}
                <SocialLinksWrapper>
                  <SocialLinks>
                    {linkedinLink && (
                      <a href={linkedinLink?.url} target={linkedinLink?.target}>
                        <LinkedinIcon />
                        <VisuallyHidden>Linkedin</VisuallyHidden>
                      </a>
                    )}
                    {twitterLink && (
                      <a href={twitterLink?.url} target={twitterLink?.target}>
                        <TwitterIcon />
                        <VisuallyHidden>Twitter</VisuallyHidden>
                      </a>
                    )}
                  </SocialLinks>
                </SocialLinksWrapper>
              </SpeakerContainer>
            );
          })}
        </SectionGrid>
      </Container>

      <IllustrationWrapper>
        <ImageContainer>
          <BgIllustration />
        </ImageContainer>
      </IllustrationWrapper>
    </SpeakersSection>
  );
}
